<div class="flex flex-col gap-4">
  <!-- Instructions -->
  <div class="p-3 rounded-lg border border-info bg-info-low-contrast flex gap-2.5">
    <lucide-icon name="Info" class="w-4 h-4 text-info flex-shrink-0 mt-0.5"></lucide-icon>
    <app-text size="xs" color="info">
      Update your load progress by completing actions as you reach each milestone. Each action is saved immediately.
    </app-text>
  </div>

  <!-- Stop Sections -->
  @for (stopForm of stopForms(); track stopForm.stop.stopNumber; let index = $index) {
  <div
    class="border rounded-lg overflow-hidden transition-all"
    [ngClass]="{
      'border-success bg-success-low-contrast': stopForm.isCompleted,
      'border-primary bg-primary-low-contrast': stopForm.isCurrentStop,
      'border-border bg-surface': !stopForm.isCompleted && !stopForm.isCurrentStop
    }"
  >
    <!-- Stop Header -->
    <button
      type="button"
      (click)="toggleStop(index)"
      class="w-full p-3 flex items-center gap-3 hover:bg-surface-high transition-colors"
    >
      <!-- Stop Icon -->
      <div
        class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        [ngClass]="{
          'bg-success text-success-foreground': stopForm.isCompleted,
          'bg-primary text-primary-foreground': stopForm.isCurrentStop && !stopForm.isCompleted,
          'bg-secondary text-secondary-foreground': !stopForm.isCurrentStop && !stopForm.isCompleted
        }"
      >
        @if (stopForm.isCompleted) {
        <lucide-icon name="CheckCircle2" class="w-4 h-4"></lucide-icon>
        } @else {
        <lucide-icon [name]="getStopIcon(stopForm.stop)" class="w-4 h-4"></lucide-icon>
        }
      </div>

      <!-- Stop Info -->
      <div class="flex-1 text-left min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <app-text size="sm" weight="semibold">
            Stop {{ stopForm.stop.stopNumber }} - {{ stopForm.stop.stopType | enumLabel : "StopType" | titlecase }}
          </app-text>
          @if (stopForm.isCurrentStop && !stopForm.isCompleted) {
          <app-badge variant="primary" size="xs" title="Current" prefixIcon="MapPin"></app-badge>
          } @if (stopForm.stop.status) {
          <app-badge
            [variant]="getStopStatusColor(stopForm.stop)"
            size="xs"
            [title]="stopForm.stop.status | enumLabel : 'StopStatusType'"
          ></app-badge>
          }
        </div>

        <div class="flex items-center gap-3 mt-0.5 flex-wrap">
          <app-text size="xs" color="muted">
            {{ stopForm.stop.companyName || "Unknown Company" }}
          </app-text>

          <!-- Appointment Time -->
          @if (stopForm.stop.appointment?.from) {
          <div class="flex items-center gap-1">
            <lucide-icon name="Clock" class="w-3 h-3 text-muted-foreground"></lucide-icon>
            <app-text size="xs" color="muted">
              {{ formatAppointmentTime(stopForm.stop.appointment?.from) }}
            </app-text>
          </div>
          }
        </div>
      </div>

      <lucide-icon
        [name]="stopForm.isExpanded ? 'ChevronUp' : 'ChevronDown'"
        class="w-5 h-5 text-muted-foreground"
      ></lucide-icon>
    </button>

    <!-- Stop Form (Collapsible) -->
    @if (stopForm.isExpanded) {
    <div class="border-t border-border bg-background p-4" [formGroup]="stopForm.form">
      <div class="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <!-- LEFT: Completed milestones & Documents (read-only with inline edit) -->
        <div class="space-y-4">
          <div>
            <app-text size="xs" weight="semibold" class="text-muted-foreground uppercase tracking-wide block mb-3">
              Milestones
            </app-text>

            <div class="space-y-2">
              <!-- ETA -->
              @if (stopForm.stop.etaDateTime?.date && stopForm.stop.etaDateTime?.time) {
                <div class="flex items-center justify-between py-2 px-3 rounded-lg bg-surface group">
                  <div class="flex items-center gap-2 min-w-0">
                    <lucide-icon name="Clock" class="w-4 h-4 text-muted-foreground flex-shrink-0"></lucide-icon>
                    <div class="min-w-0">
                      <app-text size="xs" color="muted">ETA</app-text>
                      <app-text size="sm" weight="medium" class="truncate">{{ formatTimestamp(stopForm.stop.etaDateTime, stopForm.stop) }}</app-text>
                    </div>
                  </div>
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Pencil"
                    [iconOnly]="true"
                    title="Edit ETA"
                    (click)="startMilestoneEdit(stopForm.stop.stopNumber, 'etaDateTime', stopForm.stop.etaDateTime)"
                  ></app-button>
                </div>

                @if (isEditingMilestone(stopForm.stop.stopNumber, 'etaDateTime')) {
                  <div class="p-3 rounded-lg border border-border bg-surface-high space-y-3">
                    <app-text size="xs" weight="semibold">Edit ETA</app-text>
                    <app-datetime-picker [formControl]="milestoneDateTimeControl" orientation="vertical"></app-datetime-picker>
                    <div class="flex gap-2 justify-end">
                      <app-button variant="ghost" size="xs" (click)="cancelMilestoneEdit()">Cancel</app-button>
                      <app-button variant="primary" size="xs" [loading]="isSavingMilestone()" (click)="saveMilestoneEdit(stopForm.stop.stopNumber, 'etaDateTime')">Save</app-button>
                    </div>
                  </div>
                }
              }

              <!-- Arrived -->
              @if (stopForm.stop.arrivedAt || stopForm.stop.arrivalDateTime) {
                <div class="flex items-center justify-between py-2 px-3 rounded-lg bg-surface group">
                  <div class="flex items-center gap-2 min-w-0">
                    <lucide-icon name="MapPinCheck" class="w-4 h-4 text-muted-foreground flex-shrink-0"></lucide-icon>
                    <div class="min-w-0">
                      <app-text size="xs" color="muted">Arrived</app-text>
                      <app-text size="sm" weight="medium" class="truncate">{{ formatTimestamp(stopForm.stop.arrivedAt || stopForm.stop.arrivalDateTime, stopForm.stop) }}</app-text>
                    </div>
                  </div>
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Pencil"
                    [iconOnly]="true"
                    title="Edit Arrival Time"
                    (click)="startMilestoneEdit(stopForm.stop.stopNumber, 'arrivedAt', stopForm.stop.arrivedAt || stopForm.stop.arrivalDateTime)"
                  ></app-button>
                </div>

                @if (isEditingMilestone(stopForm.stop.stopNumber, 'arrivedAt')) {
                  <div class="p-3 rounded-lg border border-border bg-surface-high space-y-3">
                    <app-text size="xs" weight="semibold">Edit Arrival Time</app-text>
                    <app-datetime-picker [formControl]="milestoneDateTimeControl" orientation="vertical"></app-datetime-picker>
                    <div class="flex gap-2 justify-end">
                      <app-button variant="ghost" size="xs" (click)="cancelMilestoneEdit()">Cancel</app-button>
                      <app-button variant="primary" size="xs" [loading]="isSavingMilestone()" (click)="saveMilestoneEdit(stopForm.stop.stopNumber, 'arrivedAt')">Save</app-button>
                    </div>
                  </div>
                }
              }

              <!-- Checked In -->
              @if (stopForm.stop.checkedInAt || stopForm.stop.checkInDateTime) {
                <div class="flex items-center justify-between py-2 px-3 rounded-lg bg-surface group">
                  <div class="flex items-center gap-2 min-w-0">
                    <lucide-icon name="LogIn" class="w-4 h-4 text-muted-foreground flex-shrink-0"></lucide-icon>
                    <div class="min-w-0">
                      <app-text size="xs" color="muted">Checked In</app-text>
                      <app-text size="sm" weight="medium" class="truncate">{{ formatTimestamp(stopForm.stop.checkedInAt || stopForm.stop.checkInDateTime, stopForm.stop) }}</app-text>
                    </div>
                  </div>
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Pencil"
                    [iconOnly]="true"
                    title="Edit Check-In Time"
                    (click)="startMilestoneEdit(stopForm.stop.stopNumber, 'checkedInAt', stopForm.stop.checkedInAt || stopForm.stop.checkInDateTime)"
                  ></app-button>
                </div>

                @if (isEditingMilestone(stopForm.stop.stopNumber, 'checkedInAt')) {
                  <div class="p-3 rounded-lg border border-border bg-surface-high space-y-3">
                    <app-text size="xs" weight="semibold">Edit Check-In Time</app-text>
                    <app-datetime-picker [formControl]="milestoneDateTimeControl" orientation="vertical"></app-datetime-picker>
                    <div class="flex gap-2 justify-end">
                      <app-button variant="ghost" size="xs" (click)="cancelMilestoneEdit()">Cancel</app-button>
                      <app-button variant="primary" size="xs" [loading]="isSavingMilestone()" (click)="saveMilestoneEdit(stopForm.stop.stopNumber, 'checkedInAt')">Save</app-button>
                    </div>
                  </div>
                }
              }

              <!-- Dock Number / Docked -->
              @if (stopForm.stop.dockNumber || stopForm.stop.dockedAt) {
                <div class="flex items-center justify-between py-2 px-3 rounded-lg bg-surface group">
                  <div class="flex items-center gap-2 min-w-0">
                    <lucide-icon name="Hash" class="w-4 h-4 text-muted-foreground flex-shrink-0"></lucide-icon>
                    <div class="min-w-0">
                      <app-text size="xs" color="muted">Dock</app-text>
                      <app-text size="sm" weight="medium" class="truncate">
                        {{ stopForm.stop.dockNumber || 'Assigned' }}
                        @if (stopForm.stop.dockedAt) {
                          <span class="text-xs text-muted font-normal ml-1">({{ formatTimestamp(stopForm.stop.dockedAt, stopForm.stop) }})</span>
                        }
                      </app-text>
                    </div>
                  </div>
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Pencil"
                    [iconOnly]="true"
                    title="Edit Dock Number"
                    (click)="startMilestoneEdit(stopForm.stop.stopNumber, 'dockNumber', stopForm.stop.dockNumber)"
                  ></app-button>
                </div>

                @if (isEditingMilestone(stopForm.stop.stopNumber, 'dockNumber')) {
                  <div class="p-3 rounded-lg border border-border bg-surface-high space-y-3">
                    <app-text size="xs" weight="semibold">Edit Dock Number</app-text>
                    <app-input [formControl]="milestoneDockControl" placeholder="e.g., D-14 or N/A"></app-input>
                    <div class="flex gap-2 justify-end">
                      <app-button variant="ghost" size="xs" (click)="cancelMilestoneEdit()">Cancel</app-button>
                      <app-button variant="primary" size="xs" [loading]="isSavingMilestone()" (click)="saveMilestoneEdit(stopForm.stop.stopNumber, 'dockNumber')">Save</app-button>
                    </div>
                  </div>
                }
              }

              <!-- Loading / Unloading Started -->
              @if (stopForm.stop.loadingStartedAt) {
                <div class="flex items-center justify-between py-2 px-3 rounded-lg bg-surface group">
                  <div class="flex items-center gap-2 min-w-0">
                    <lucide-icon name="PackagePlus" class="w-4 h-4 text-muted-foreground flex-shrink-0"></lucide-icon>
                    <div class="min-w-0">
                      <app-text size="xs" color="muted">{{ stopForm.stop.stopType === 'delivery' ? 'Unloading Started' : 'Loading Started' }}</app-text>
                      <app-text size="sm" weight="medium" class="truncate">{{ formatTimestamp(stopForm.stop.loadingStartedAt, stopForm.stop) }}</app-text>
                    </div>
                  </div>
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Pencil"
                    [iconOnly]="true"
                    title="Edit Start Time"
                    (click)="startMilestoneEdit(stopForm.stop.stopNumber, 'loadingStartedAt', stopForm.stop.loadingStartedAt)"
                  ></app-button>
                </div>

                @if (isEditingMilestone(stopForm.stop.stopNumber, 'loadingStartedAt')) {
                  <div class="p-3 rounded-lg border border-border bg-surface-high space-y-3">
                    <app-text size="xs" weight="semibold">Edit Start Time</app-text>
                    <app-datetime-picker [formControl]="milestoneDateTimeControl" orientation="vertical"></app-datetime-picker>
                    <div class="flex gap-2 justify-end">
                      <app-button variant="ghost" size="xs" (click)="cancelMilestoneEdit()">Cancel</app-button>
                      <app-button variant="primary" size="xs" [loading]="isSavingMilestone()" (click)="saveMilestoneEdit(stopForm.stop.stopNumber, 'loadingStartedAt')">Save</app-button>
                    </div>
                  </div>
                }
              }

              <!-- Loaded / Unloaded -->
              @if (stopForm.stop.loadedAt) {
                <div class="flex items-center justify-between py-2 px-3 rounded-lg bg-surface group">
                  <div class="flex items-center gap-2 min-w-0">
                    <lucide-icon name="PackageCheck" class="w-4 h-4 text-muted-foreground flex-shrink-0"></lucide-icon>
                    <div class="min-w-0">
                      <app-text size="xs" color="muted">{{ stopForm.stop.stopType === 'delivery' ? 'Unloaded' : 'Loaded' }}</app-text>
                      <app-text size="sm" weight="medium" class="truncate">{{ formatTimestamp(stopForm.stop.loadedAt, stopForm.stop) }}</app-text>
                    </div>
                  </div>
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Pencil"
                    [iconOnly]="true"
                    title="Edit Complete Time"
                    (click)="startMilestoneEdit(stopForm.stop.stopNumber, 'loadedAt', stopForm.stop.loadedAt)"
                  ></app-button>
                </div>

                @if (isEditingMilestone(stopForm.stop.stopNumber, 'loadedAt')) {
                  <div class="p-3 rounded-lg border border-border bg-surface-high space-y-3">
                    <app-text size="xs" weight="semibold">Edit Complete Time</app-text>
                    <app-datetime-picker [formControl]="milestoneDateTimeControl" orientation="vertical"></app-datetime-picker>
                    <div class="flex gap-2 justify-end">
                      <app-button variant="ghost" size="xs" (click)="cancelMilestoneEdit()">Cancel</app-button>
                      <app-button variant="primary" size="xs" [loading]="isSavingMilestone()" (click)="saveMilestoneEdit(stopForm.stop.stopNumber, 'loadedAt')">Save</app-button>
                    </div>
                  </div>
                }
              }

              <!-- Checked Out -->
              @if (stopForm.stop.checkedOutAt || stopForm.stop.checkOutDateTime) {
                <div class="flex items-center justify-between py-2 px-3 rounded-lg bg-surface group">
                  <div class="flex items-center gap-2 min-w-0">
                    <lucide-icon name="LogOut" class="w-4 h-4 text-muted-foreground flex-shrink-0"></lucide-icon>
                    <div class="min-w-0">
                      <app-text size="xs" color="muted">Checked Out</app-text>
                      <app-text size="sm" weight="medium" class="truncate">{{ formatTimestamp(stopForm.stop.checkedOutAt || stopForm.stop.checkOutDateTime, stopForm.stop) }}</app-text>
                    </div>
                  </div>
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Pencil"
                    [iconOnly]="true"
                    title="Edit Check-Out Time"
                    (click)="startMilestoneEdit(stopForm.stop.stopNumber, 'checkedOutAt', stopForm.stop.checkedOutAt || stopForm.stop.checkOutDateTime)"
                  ></app-button>
                </div>

                @if (isEditingMilestone(stopForm.stop.stopNumber, 'checkedOutAt')) {
                  <div class="p-3 rounded-lg border border-border bg-surface-high space-y-3">
                    <app-text size="xs" weight="semibold">Edit Check-Out Time</app-text>
                    <app-datetime-picker [formControl]="milestoneDateTimeControl" orientation="vertical"></app-datetime-picker>
                    <div class="flex gap-2 justify-end">
                      <app-button variant="ghost" size="xs" (click)="cancelMilestoneEdit()">Cancel</app-button>
                      <app-button variant="primary" size="xs" [loading]="isSavingMilestone()" (click)="saveMilestoneEdit(stopForm.stop.stopNumber, 'checkedOutAt')">Save</app-button>
                    </div>
                  </div>
                }
              }

              <!-- Departed -->
              @if (stopForm.stop.departedAt || stopForm.stop.departureDateTime) {
                <div class="flex items-center justify-between py-2 px-3 rounded-lg bg-surface group">
                  <div class="flex items-center gap-2 min-w-0">
                    <lucide-icon name="ArrowRightFromLine" class="w-4 h-4 text-muted-foreground flex-shrink-0"></lucide-icon>
                    <div class="min-w-0">
                      <app-text size="xs" color="muted">Departed</app-text>
                      <app-text size="sm" weight="medium" class="truncate">{{ formatTimestamp(stopForm.stop.departedAt || stopForm.stop.departureDateTime, stopForm.stop) }}</app-text>
                    </div>
                  </div>
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Pencil"
                    [iconOnly]="true"
                    title="Edit Departure Time"
                    (click)="startMilestoneEdit(stopForm.stop.stopNumber, 'departedAt', stopForm.stop.departedAt || stopForm.stop.departureDateTime)"
                  ></app-button>
                </div>

                @if (isEditingMilestone(stopForm.stop.stopNumber, 'departedAt')) {
                  <div class="p-3 rounded-lg border border-border bg-surface-high space-y-3">
                    <app-text size="xs" weight="semibold">Edit Departure Time</app-text>
                    <app-datetime-picker [formControl]="milestoneDateTimeControl" orientation="vertical"></app-datetime-picker>
                    <div class="flex gap-2 justify-end">
                      <app-button variant="ghost" size="xs" (click)="cancelMilestoneEdit()">Cancel</app-button>
                      <app-button variant="primary" size="xs" [loading]="isSavingMilestone()" (click)="saveMilestoneEdit(stopForm.stop.stopNumber, 'departedAt')">Save</app-button>
                    </div>
                  </div>
                }
              }
            </div>
          </div>

          <!-- Documents status in left column (State only) -->
          <div class="pt-4 border-t border-border space-y-2.5">
            <app-text size="xs" weight="semibold" class="text-muted-foreground uppercase tracking-wide block">
              Documents & Photos
            </app-text>

            <div class="space-y-1.5">
              @for (doc of getStopDocuments(stopForm.stop); track doc.key) {
                <div
                  class="flex items-center justify-between p-2.5 rounded-lg bg-surface border border-border transition-all"
                  [class.hover:bg-surface-high]="doc.hasDoc"
                  [class.cursor-pointer]="doc.hasDoc"
                  (click)="doc.hasDoc && doc.url ? viewDocument(doc.url, doc.label) : null"
                  [title]="doc.hasDoc ? 'Click to view ' + doc.label : ''"
                >
                  <div class="flex items-center gap-2.5 min-w-0">
                    <span class="text-base leading-none select-none">{{ doc.emoji }}</span>
                    <app-text size="xs" [weight]="doc.hasDoc ? 'medium' : 'normal'" class="truncate">
                      {{ doc.label }}
                    </app-text>
                  </div>

                  <!-- Status icon -->
                  <div class="flex items-center flex-shrink-0">
                    @if (doc.status === 'approved') {
                      <div class="w-6 h-6 rounded-full bg-success-low-contrast flex items-center justify-center text-success" title="Approved">
                        <lucide-icon name="CheckCheck" class="w-3.5 h-3.5"></lucide-icon>
                      </div>
                    } @else if (doc.status === 'rejected') {
                      <div class="w-6 h-6 rounded-full bg-danger-low-contrast flex items-center justify-center text-danger" title="Rejected">
                        <lucide-icon name="CircleX" class="w-3.5 h-3.5"></lucide-icon>
                      </div>
                    } @else if (doc.hasDoc && doc.status === 'pending') {
                      <div class="w-6 h-6 rounded-full bg-warning-low-contrast flex items-center justify-center text-warning" title="Pending Review">
                        <lucide-icon name="Clock" class="w-3.5 h-3.5"></lucide-icon>
                      </div>
                    } @else if (doc.isNA) {
                      <div class="w-6 h-6 rounded-full bg-surface-high flex items-center justify-center text-muted" title="Not Applicable">
                        <lucide-icon name="CircleSlash" class="w-3.5 h-3.5"></lucide-icon>
                      </div>
                    } @else {
                      <div class="w-6 h-6 rounded-full bg-surface-high flex items-center justify-center text-muted-foreground/50" title="Missing">
                        <lucide-icon name="Circle" class="w-3 h-3"></lucide-icon>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
        
        <!-- RIGHT: Next Action -->
        <div class="border-l border-border pl-6 min-h-[200px]">
          @if (stopForm.isCompleted) {
            <div class="flex flex-col items-center justify-center py-8 text-center bg-surface rounded-lg border border-border">
              <div class="w-12 h-12 rounded-full bg-success-low-contrast flex items-center justify-center mb-3">
                <lucide-icon name="CheckCircle2" class="w-6 h-6 text-success"></lucide-icon>
              </div>
              <app-text size="sm" weight="semibold">Stop Complete!</app-text>
            </div>
          } @else if (!stopForm.isCurrentStop) {
            <div class="flex flex-col items-center justify-center py-8 text-center bg-surface rounded-lg border border-border">
              <div class="w-12 h-12 rounded-full bg-surface-high flex items-center justify-center mb-3">
                <lucide-icon name="Clock" class="w-6 h-6 text-muted-foreground"></lucide-icon>
              </div>
              <app-text size="sm" weight="semibold">Waiting for Stop {{ currentStopIndex() + 1 }}</app-text>
              <app-text size="xs" color="muted" class="mt-1 max-w-[260px]">
                This stop will become active once previous stops have departed.
              </app-text>
            </div>
          } @else if (!stopForm.stop.status || stopForm.stop.status === 'PENDING') {
            <!-- Provide ETA to Pickup / Stop -->
            <div class="space-y-4">
              <div>
                <app-text size="xs" weight="semibold" class="text-muted-foreground uppercase tracking-wide block mb-1">
                  Next Action: Provide ETA
                </app-text>
                <app-text size="sm" color="muted">
                  Set the estimated arrival date and time for Stop {{ stopForm.stop.stopNumber }} ({{ stopForm.stop.stopType | enumLabel : 'StopType' | titlecase }}).
                </app-text>
              </div>

              <div class="p-4 rounded-lg bg-surface border border-border space-y-4">
                <div class="space-y-2">
                  <app-text size="xs" weight="semibold">Estimated Time of Arrival (ETA)</app-text>
                  <app-datetime-picker formControlName="etaDateTime" orientation="horizontal"></app-datetime-picker>
                </div>

                <div class="pt-2 flex flex-col gap-2">
                  <app-button
                    variant="primary"
                    icon="Clock"
                    class="w-full justify-start"
                    [loading]="actionLoading()?.action === 'START_EN_ROUTE'"
                    [disabled]="!!actionLoading()"
                    (click)="provideEta(stopForm.stop.stopNumber)"
                  >
                    Provide ETA & Set En Route
                  </app-button>

                  <div class="flex justify-end pt-1">
                    <app-button
                      variant="ghost"
                      size="xs"
                      icon="MapPinCheck"
                      (click)="executeStopAction(stopForm.stop.stopNumber, 'ARRIVE')"
                      title="Directly mark arrived if driver is already at the stop"
                    >
                      Or mark arrived directly
                    </app-button>
                  </div>
                </div>
              </div>
            </div>
          } @else {
            <!-- Document Approval Progress Card (Shown during review/docked/loading stages) -->
            @if (isReviewStage(stopForm.stop)) {
              @let stats = getDocumentApprovalStats(stopForm.stop);

              <div class="p-4 rounded-lg bg-surface border border-border space-y-3 mb-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <lucide-icon name="FileCheck" class="w-4 h-4 text-primary flex-shrink-0"></lucide-icon>
                    <app-text size="sm" weight="semibold">Document Approval Progress</app-text>
                  </div>
                  <app-text size="xs" weight="medium" [color]="stats.approved === stats.total ? 'primary' : 'muted'">
                    {{ stats.approved }} of {{ stats.total }} Cleared
                  </app-text>
                </div>

                <!-- Multi-segmented Progress Bar with Slanted Ends -->
                <div class="w-full h-3 rounded-lg bg-surface-high/60 p-0.5 border border-border flex gap-1.5 overflow-hidden">
                  @if (stats.approvedPercent > 0) {
                    <div
                      class="bg-success h-full -skew-x-12 rounded-xs transition-all duration-500 shadow-xs"
                      [style.width.%]="stats.approvedPercent"
                      title="Approved: {{ stats.approvedPercent }}%"
                    ></div>
                  }
                  @if (stats.pendingPercent > 0) {
                    <div
                      class="bg-warning h-full -skew-x-12 rounded-xs transition-all duration-500 shadow-xs"
                      [style.width.%]="stats.pendingPercent"
                      title="Pending: {{ stats.pendingPercent }}%"
                    ></div>
                  }
                  @if (stats.rejectedPercent > 0) {
                    <div
                      class="bg-danger h-full -skew-x-12 rounded-xs transition-all duration-500 shadow-xs"
                      [style.width.%]="stats.rejectedPercent"
                      title="Rejected: {{ stats.rejectedPercent }}%"
                    ></div>
                  }
                </div>

                <!-- Stats breakdown badges -->
                <div class="flex flex-wrap items-center gap-2">
                  <app-badge variant="success" badgeStyle="soft" prefixIcon="CheckCircle2" size="xs">{{ stats.approved }} Approved</app-badge>
                  @if (stats.pending > 0) {
                    <app-badge variant="warning" badgeStyle="soft" prefixIcon="Clock" size="xs">{{ stats.pending }} Pending</app-badge>
                  }
                  @if (stats.rejected > 0) {
                    <app-badge variant="danger" badgeStyle="soft" prefixIcon="FileX" size="xs">{{ stats.rejected }} Rejected</app-badge>
                  }
                  @if (stats.missing > 0) {
                    <app-badge variant="default" badgeStyle="outline" prefixIcon="CircleAlert" size="xs">{{ stats.missing }} Missing</app-badge>
                  }
                </div>

                <!-- Contextual Actions & Alerts -->
                @if (stats.missing > 0) {
                  <div class="p-2.5 rounded bg-surface border border-warning text-xs space-y-1">
                    <div class="flex items-center gap-1.5 font-medium text-warning">
                      <lucide-icon name="CircleAlert" class="w-3.5 h-3.5 flex-shrink-0"></lucide-icon>
                      <span>Upload Required Documents</span>
                    </div>
                    <app-text size="xs" color="muted">
                      Missing: {{ getMissingRequiredDocuments(stopForm.stop).join(', ') }}. Upload or mark N/A below.
                    </app-text>
                  </div>
                } @else if (stats.rejected > 0) {
                  <div class="p-2.5 rounded bg-surface border border-danger text-xs space-y-1">
                    <div class="flex items-center gap-1.5 font-medium text-danger">
                      <lucide-icon name="FileX" class="w-3.5 h-3.5 flex-shrink-0"></lucide-icon>
                      <span>Rejected Documents Require Replacement</span>
                    </div>
                    @for (rej of getRejectedDocuments(stopForm.stop); track rej.docKey) {
                      <div class="text-[11px] text-danger">
                        • <strong>{{ rej.label }}:</strong> {{ rej.reason || 'Needs replacement' }}
                      </div>
                    }
                  </div>
                } @else if (stats.pending > 0) {
                  <div class="pt-1 flex items-center justify-between gap-2">
                    <app-text size="xs" color="muted">
                      Review docs on left or:
                    </app-text>
                    <app-button
                      variant="success"
                      shape="pill"
                      size="xs"
                      icon="CheckCheck"
                      [loading]="actionLoading()?.stopNumber === stopForm.stop.stopNumber && actionLoading()?.action === 'APPROVE'"
                      [disabled]="!!actionLoading()"
                      (click)="approveAllDocuments(stopForm.stop.stopNumber)"
                    >
                      Approve All Pending
                    </app-button>
                  </div>
                } @else if (areAllDocumentsApproved(stopForm.stop)) {
                  <div class="p-2.5 rounded-full bg-surface border border-success/40 text-xs flex items-center gap-2 shadow-xs">
                    <lucide-icon name="CheckCircle2" class="w-4 h-4 text-success flex-shrink-0"></lucide-icon>
                    <app-text size="xs" weight="medium">All documents approved. Proceed to Check Out below.</app-text>
                  </div>
                }
              </div>
            }

            @if (getNextActions(stopForm.stop).length > 0) {
              <app-text size="xs" weight="semibold" class="text-muted-foreground uppercase tracking-wide block mb-3">
                Next Actions
              </app-text>
              @for (action of getNextActions(stopForm.stop); track action.action) {
                @if (action.action === 'REJECT_DOCS' && rejectingStopNumber() === stopForm.stop.stopNumber) {
                  <div class="p-3 mb-4 rounded-xl bg-surface border border-danger/40 space-y-3 shadow-sm">
                    <div class="flex items-center gap-2">
                      <lucide-icon name="FileX" class="w-4 h-4 text-danger"></lucide-icon>
                      <app-text size="xs" weight="semibold" color="danger">Reject Documents</app-text>
                    </div>
                    <app-input
                      placeholder="Reason for rejection (e.g., blurry photo, missing signature)"
                      [value]="rejectionReason()"
                      (valueChange)="rejectionReason.set($event ?? '')"
                    ></app-input>
                    <div class="flex gap-2 pt-2">
                      <app-button
                        variant="danger"
                        shape="pill"
                        size="sm"
                        icon="FileX"
                        [loading]="actionLoading()?.stopNumber === stopForm.stop.stopNumber && actionLoading()?.action === 'REJECT_DOCS'"
                        [disabled]="!!actionLoading()"
                        (click)="confirmRejectDocs(stopForm.stop.stopNumber)"
                      >
                        Confirm Rejection
                      </app-button>
                      <app-button
                        variant="ghost"
                        shape="pill"
                        size="sm"
                        [disabled]="!!actionLoading()"
                        (click)="rejectingStopNumber.set(null); rejectionReason.set('')"
                      >
                        Cancel
                      </app-button>
                    </div>
                  </div>
                } @else {
                  <div class="mb-4 p-3.5 rounded-xl border border-border bg-surface space-y-3 shadow-xs">
                    @if (action.action !== 'APPROVE' && action.action !== 'REJECT_DOCS') {
                      <div class="space-y-1.5">
                        <div class="flex items-center justify-between">
                          <app-text size="xs" weight="medium" color="muted" class="flex items-center gap-1.5">
                            <lucide-icon name="Clock" class="w-3.5 h-3.5"></lucide-icon>
                            {{ action.action === 'CHECK_IN' || action.action === 'CHECK_OUT' ? 'Milestone Date & Time (Required)' : 'Milestone Date & Time' }}
                          </app-text>
                          @if (isSkippableAction(action.action)) {
                            <app-button
                              variant="ghost"
                              size="xs"
                              icon="FastForward"
                              (click)="skipAction(stopForm.stop.stopNumber, action.action)"
                              [disabled]="!!actionLoading()"
                              title="Skip this non-essential milestone"
                            >
                              Skip
                            </app-button>
                          }
                        </div>
                        <app-datetime-picker
                          [formControl]="actionDateTimeControls[stopForm.stop.stopNumber!]"
                          orientation="horizontal"
                        ></app-datetime-picker>
                      </div>
                    }

                    <div class="flex flex-col gap-2 pt-1">
                      <app-button
                        [variant]="action.action === 'REJECT_DOCS' ? 'danger' : (action.action === 'APPROVE' ? 'success' : 'primary')"
                        shape="pill"
                        size="md"
                        [icon]="action.icon"
                        class="w-full"
                        [loading]="actionLoading()?.stopNumber === stopForm.stop.stopNumber && actionLoading()?.action === action.action"
                        [disabled]="isActionDisabled(stopForm.stop, action.action)"
                        (click)="handleActionClick(stopForm.stop.stopNumber, action.action)"
                      >
                        {{ action.label }}
                      </app-button>

                      @if (action.action === 'CHECK_OUT' && !areAllDocumentsApproved(stopForm.stop)) {
                        <div class="flex items-center gap-1.5 p-2 rounded-lg bg-surface border border-warning text-xs text-warning">
                          <lucide-icon name="Lock" class="w-3.5 h-3.5 shrink-0"></lucide-icon>
                          <span>Checkout is locked until all required documents are uploaded and approved.</span>
                        </div>
                      }
                    </div>
                  </div>
                }
              }
            } @else if (stopForm.stop.status === 'WAITING_APPROVAL' && !areAllDocumentsApproved(stopForm.stop)) {
              <div class="p-3 mb-4 rounded-lg bg-surface border border-border text-xs text-muted">
                Document review in progress. Once documents are approved, Check Out will be available.
              </div>
            }

            <!-- Dock number input (shown at CHECKED_IN) -->
            @if (stopForm.stop.status === 'CHECKED_IN') {
              <div class="space-y-4 mb-4 pt-2 border-t border-border">
                <div class="space-y-2">
                  <app-text size="sm" weight="medium">Enter Dock Number</app-text>
                  <app-input
                    formControlName="dockNumber"
                    placeholder="e.g., D-14"
                  ></app-input>
                </div>
                <div class="mt-2">
                  <app-checkbox formControlName="noDockNumber" label="No dock number available"></app-checkbox>
                </div>
              </div>
            }

            <!-- Document Management (shown at appropriate steps) -->
            @if (stopForm.stop.status === 'DOCKED' || stopForm.stop.status === 'CHECKED_OUT' || stopForm.stop.status === 'DEPARTED' || stopForm.stop.status === 'LOADING' || stopForm.stop.status === 'UNLOADING' || stopForm.stop.status === 'LOADED' || stopForm.stop.status === 'WAITING_APPROVAL' || stopForm.stop.status === 'REJECT_DOCS' || stopForm.stop.status === 'APPROVED') {
              <div class="space-y-3 mt-6 pt-4 border-t border-border">
                <div>
                  <app-text size="xs" weight="semibold" class="text-muted-foreground uppercase tracking-wide block">
                    {{ stopForm.stop.stopType === 'delivery' ? 'Delivery Documents' : 'Pickup Documents' }}
                  </app-text>
                  <app-text size="xs" color="muted">
                    Uploaded documents appear as clickable previews. Click any document to view inside the viewer.
                  </app-text>
                </div>

                <div class="space-y-3 pt-1">
                  @if (stopForm.stop.stopType === 'pickup' || stopForm.stop.stopType === 'both') {
                    <!-- BOL Card -->
                    @let bolUrl = getDocPreviewUrl(stopForm, 'billOfLading', 'bol');
                    @let bolDoc = stopForm.stop.documents?.billOfLading;

                    <input
                      #bolInput
                      type="file"
                      class="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      (change)="handleDirectDocUpload($event, stopForm.stop.stopNumber, 'billOfLading', 'bol', 'documents')"
                    />

                    @if (bolUrl) {
                      <div class="p-3 rounded-xl border border-border bg-surface transition-all space-y-2.5">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div
                            role="button"
                            tabindex="0"
                            [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'billOfLading')"
                            [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'billOfLading')"
                            (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'billOfLading')"
                            (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'billOfLading')"
                            (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'billOfLading', 'bol', 'documents')"
                            (click)="viewDocument(bolUrl, 'Bill of Lading')"
                            (keydown.enter)="viewDocument(bolUrl, 'Bill of Lading')"
                            class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high hover:bg-surface-low border border-border text-left transition-all group cursor-pointer"
                            title="Click to view in viewer, or drag & drop file to replace"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                📋
                              </div>
                              <div class="min-w-0">
                                <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                  Bill of Lading
                                </app-text>
                                <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                  <lucide-icon name="Eye" class="w-3 h-3"></lucide-icon> Click to view in viewer
                                </app-text>
                              </div>
                            </div>

                            <div class="flex-shrink-0 ml-3">
                              @if (bolDoc?.status === 'approved') {
                                <app-badge variant="success" badgeStyle="soft" prefixIcon="CheckCircle2" size="xs">Approved</app-badge>
                              } @else if (bolDoc?.status === 'rejected') {
                                <app-badge variant="danger" badgeStyle="soft" prefixIcon="FileX" size="xs">Rejected</app-badge>
                              } @else {
                                <app-badge variant="warning" badgeStyle="soft" prefixIcon="Clock" size="xs">Pending</app-badge>
                              }
                            </div>
                          </div>

                          <div class="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                            @if (bolDoc?.status === 'rejected') {
                              <app-button
                                variant="danger"
                                shape="pill"
                                size="xs"
                                icon="UploadCloud"
                                [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'billOfLading'"
                                (click)="bolInput.click()"
                                title="Select file to resubmit"
                              >
                                Resubmit
                              </app-button>
                            } @else {
                              @if (isReviewStage(stopForm.stop)) {
                                @if (bolDoc?.status !== 'approved') {
                                  <app-button
                                    variant="success"
                                    shape="pill"
                                    size="xs"
                                    icon="Check"
                                    title="Approve Bill of Lading"
                                    (click)="approveSingleDocument(stopForm.stop.stopNumber, 'billOfLading')"
                                  >
                                    Approve
                                  </app-button>
                                }
                                <app-button
                                  variant="danger"
                                  shape="pill"
                                  size="xs"
                                  icon="FileX"
                                  title="Reject Bill of Lading"
                                  (click)="startDocRejection(stopForm.stop.stopNumber, 'billOfLading')"
                                >
                                  Reject
                                </app-button>
                              }

                              <app-button
                                variant="secondary"
                                shape="pill"
                                size="xs"
                                icon="RotateCcw"
                                [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'billOfLading'"
                                (click)="bolInput.click()"
                                title="Replace this document"
                              >
                                Replace
                              </app-button>
                            }
                          </div>
                        </div>

                        @if (bolDoc?.status === 'rejected' && bolDoc?.rejectionReason) {
                          <div class="p-2.5 rounded-lg bg-surface-high border border-danger/40 text-xs text-danger flex items-start gap-2">
                            <lucide-icon name="FileX" class="w-4 h-4 flex-shrink-0 mt-0.5"></lucide-icon>
                            <div><strong>Rejection reason:</strong> {{ bolDoc.rejectionReason }}</div>
                          </div>
                        }

                        @if (rejectingDoc()?.stopNumber === stopForm.stop.stopNumber && rejectingDoc()?.docKey === 'billOfLading') {
                          <div class="p-3.5 rounded-xl bg-surface border border-danger/40 space-y-3 shadow-sm">
                            <div class="flex items-center gap-2">
                              <lucide-icon name="FileX" class="w-4 h-4 text-danger"></lucide-icon>
                              <app-text size="xs" weight="semibold" color="danger">Specify reason for rejecting Bill of Lading</app-text>
                            </div>
                            <app-input
                              [value]="docRejectionReason()"
                              (valueChange)="docRejectionReason.set($event ?? '')"
                              placeholder="e.g., blurry photo, missing signature, seal # cut off"
                            ></app-input>
                            <div class="flex gap-2 justify-end pt-2">
                              <app-button variant="ghost" shape="pill" size="xs" (click)="cancelDocRejection()">Cancel</app-button>
                              <app-button variant="danger" shape="pill" size="xs" icon="FileX" (click)="confirmDocRejection(stopForm.stop.stopNumber, 'billOfLading')">Confirm Reject</app-button>
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="p-3 rounded-xl border border-border bg-surface transition-all">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [formGroup]="stopForm.form">
                          <div
                            role="button"
                            tabindex="0"
                            [class.opacity-60]="stopForm.form.get('noBol')?.value"
                            [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'billOfLading')"
                            [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'billOfLading')"
                            (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'billOfLading')"
                            (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'billOfLading')"
                            (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'billOfLading', 'bol', 'documents')"
                            (click)="!stopForm.form.get('noBol')?.value && bolInput.click()"
                            (keydown.enter)="!stopForm.form.get('noBol')?.value && bolInput.click()"
                            class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high border border-dashed border-border text-left transition-all group cursor-pointer"
                            [title]="stopForm.form.get('noBol')?.value ? 'Document marked as not available' : 'Click or drag & drop to upload file'"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                📋
                              </div>
                              <div class="min-w-0">
                                <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                  Bill of Lading
                                </app-text>
                                <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                  @if (uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'billOfLading') {
                                    <lucide-icon name="UploadCloud" class="w-3 h-3 animate-bounce text-primary"></lucide-icon>
                                    Uploading {{ uploadingDoc()?.progress }}%...
                                  } @else if (stopForm.form.get('noBol')?.value) {
                                    Not required / available
                                  } @else {
                                    <lucide-icon name="UploadCloud" class="w-3 h-3"></lucide-icon> Click or drag & drop to upload (PDF, PNG, JPG)
                                  }
                                </app-text>
                              </div>
                            </div>

                            <div class="flex-shrink-0 ml-3">
                              @if (stopForm.form.get('noBol')?.value) {
                                <app-badge variant="default" badgeStyle="soft" size="xs">N/A</app-badge>
                              } @else {
                                <app-badge variant="default" badgeStyle="outline" size="xs">Missing</app-badge>
                              }
                            </div>
                          </div>

                          <div class="flex items-center gap-3 flex-shrink-0 self-end sm:self-auto">
                            <app-button
                              variant="primary"
                              shape="pill"
                              size="xs"
                              icon="UploadCloud"
                              [disabled]="stopForm.form.get('noBol')?.value"
                              [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'billOfLading'"
                              (click)="bolInput.click()"
                              title="Select file to upload"
                            >
                              Upload
                            </app-button>
                            <app-checkbox
                              formControlName="noBol"
                              label="N/A"
                              class="block"
                            ></app-checkbox>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Load Photo Card -->
                    @let loadPhotoUrl = getDocPreviewUrl(stopForm, 'loadImage', 'loadImage');
                    @let loadPhotoDoc = stopForm.stop.documents?.loadImage;

                    <input
                      #loadImageInput
                      type="file"
                      class="hidden"
                      accept="image/*"
                      (change)="handleDirectDocUpload($event, stopForm.stop.stopNumber, 'loadImage', 'loadImage', 'images')"
                    />

                    @if (loadPhotoUrl) {
                      <div class="p-3 rounded-xl border border-border bg-surface transition-all space-y-2.5">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div
                            role="button"
                            tabindex="0"
                            [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'loadImage')"
                            [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'loadImage')"
                            (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'loadImage')"
                            (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'loadImage')"
                            (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'loadImage', 'loadImage', 'images')"
                            (click)="viewDocument(loadPhotoUrl, 'Load Photo')"
                            (keydown.enter)="viewDocument(loadPhotoUrl, 'Load Photo')"
                            class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high hover:bg-surface-low border border-border text-left transition-all group cursor-pointer"
                            title="Click to view Load Photo in viewer, or drag & drop file to replace"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                📦
                              </div>
                              <div class="min-w-0">
                                <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                  Load Photo
                                </app-text>
                                <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                  <lucide-icon name="Eye" class="w-3 h-3"></lucide-icon> Click to view in viewer
                                </app-text>
                              </div>
                            </div>

                            <div class="flex-shrink-0 ml-3">
                              @if (loadPhotoDoc?.status === 'approved') {
                                <app-badge variant="success" badgeStyle="soft" prefixIcon="CheckCircle2" size="xs">Approved</app-badge>
                              } @else if (loadPhotoDoc?.status === 'rejected') {
                                <app-badge variant="danger" badgeStyle="soft" prefixIcon="FileX" size="xs">Rejected</app-badge>
                              } @else {
                                <app-badge variant="warning" badgeStyle="soft" prefixIcon="Clock" size="xs">Pending</app-badge>
                              }
                            </div>
                          </div>

                          <div class="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                            @if (loadPhotoDoc?.status === 'rejected') {
                              <app-button
                                variant="danger"
                                shape="pill"
                                size="xs"
                                icon="UploadCloud"
                                [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'loadImage'"
                                (click)="loadImageInput.click()"
                                title="Select file to resubmit"
                              >
                                Resubmit
                              </app-button>
                            } @else {
                              @if (isReviewStage(stopForm.stop)) {
                                @if (loadPhotoDoc?.status !== 'approved') {
                                  <app-button
                                    variant="success"
                                    shape="pill"
                                    size="xs"
                                    icon="Check"
                                    title="Approve Load Photo"
                                    (click)="approveSingleDocument(stopForm.stop.stopNumber, 'loadImage')"
                                  >
                                    Approve
                                  </app-button>
                                }
                                <app-button
                                  variant="danger"
                                  shape="pill"
                                  size="xs"
                                  icon="FileX"
                                  title="Reject Load Photo"
                                  (click)="startDocRejection(stopForm.stop.stopNumber, 'loadImage')"
                                >
                                  Reject
                                </app-button>
                              }

                              <app-button
                                variant="secondary"
                                shape="pill"
                                size="xs"
                                icon="RotateCcw"
                                [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'loadImage'"
                                (click)="loadImageInput.click()"
                                title="Replace this document"
                              >
                                Replace
                              </app-button>
                            }
                          </div>
                        </div>

                        @if (loadPhotoDoc?.status === 'rejected' && loadPhotoDoc?.rejectionReason) {
                          <div class="p-2.5 rounded-lg bg-surface-high border border-danger/40 text-xs text-danger flex items-start gap-2">
                            <lucide-icon name="FileX" class="w-4 h-4 flex-shrink-0 mt-0.5"></lucide-icon>
                            <div><strong>Rejection reason:</strong> {{ loadPhotoDoc.rejectionReason }}</div>
                          </div>
                        }

                        @if (rejectingDoc()?.stopNumber === stopForm.stop.stopNumber && rejectingDoc()?.docKey === 'loadImage') {
                          <div class="p-3.5 rounded-xl bg-surface border border-danger/40 space-y-3 shadow-sm">
                            <div class="flex items-center gap-2">
                              <lucide-icon name="FileX" class="w-4 h-4 text-danger"></lucide-icon>
                              <app-text size="xs" weight="semibold" color="danger">Specify reason for rejecting Load Photo</app-text>
                            </div>
                            <app-input
                              [value]="docRejectionReason()"
                              (valueChange)="docRejectionReason.set($event ?? '')"
                              placeholder="e.g., cargo blurry, load improperly secured"
                            ></app-input>
                            <div class="flex gap-2 justify-end pt-2">
                              <app-button variant="ghost" shape="pill" size="xs" (click)="cancelDocRejection()">Cancel</app-button>
                              <app-button variant="danger" shape="pill" size="xs" icon="FileX" (click)="confirmDocRejection(stopForm.stop.stopNumber, 'loadImage')">Confirm Reject</app-button>
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="p-3 rounded-xl border border-border bg-surface transition-all">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [formGroup]="stopForm.form">
                          <div
                            role="button"
                            tabindex="0"
                            [class.opacity-60]="stopForm.form.get('noLoadImage')?.value"
                            [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'loadImage')"
                            [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'loadImage')"
                            (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'loadImage')"
                            (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'loadImage')"
                            (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'loadImage', 'loadImage', 'images')"
                            (click)="!stopForm.form.get('noLoadImage')?.value && loadImageInput.click()"
                            (keydown.enter)="!stopForm.form.get('noLoadImage')?.value && loadImageInput.click()"
                            class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high border border-dashed border-border text-left transition-all group cursor-pointer"
                            [title]="stopForm.form.get('noLoadImage')?.value ? 'Document marked as not available' : 'Click or drag & drop to upload file'"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                📦
                              </div>
                              <div class="min-w-0">
                                <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                  Load Photo
                                </app-text>
                                <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                  @if (uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'loadImage') {
                                    <lucide-icon name="UploadCloud" class="w-3 h-3 animate-bounce text-primary"></lucide-icon>
                                    Uploading {{ uploadingDoc()?.progress }}%...
                                  } @else if (stopForm.form.get('noLoadImage')?.value) {
                                    Not required / available
                                  } @else {
                                    <lucide-icon name="UploadCloud" class="w-3 h-3"></lucide-icon> Click or drag & drop to upload (Image)
                                  }
                                </app-text>
                              </div>
                            </div>

                            <div class="flex-shrink-0 ml-3">
                              @if (stopForm.form.get('noLoadImage')?.value) {
                                <app-badge variant="default" badgeStyle="soft" size="xs">N/A</app-badge>
                              } @else {
                                <app-badge variant="default" badgeStyle="outline" size="xs">Missing</app-badge>
                              }
                            </div>
                          </div>

                          <div class="flex items-center gap-3 flex-shrink-0 self-end sm:self-auto">
                            <app-button
                              variant="primary"
                              shape="pill"
                              size="xs"
                              icon="UploadCloud"
                              [disabled]="stopForm.form.get('noLoadImage')?.value"
                              [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'loadImage'"
                              (click)="loadImageInput.click()"
                              title="Select file to upload"
                            >
                              Upload
                            </app-button>
                            <app-checkbox
                              formControlName="noLoadImage"
                              label="N/A"
                              class="block"
                            ></app-checkbox>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Seal Photo Card -->
                    @let sealPhotoUrl = getDocPreviewUrl(stopForm, 'sealImage', 'sealImage');
                    @let sealPhotoDoc = stopForm.stop.documents?.sealImage;

                    <input
                      #sealImageInput
                      type="file"
                      class="hidden"
                      accept="image/*"
                      (change)="handleDirectDocUpload($event, stopForm.stop.stopNumber, 'sealImage', 'sealImage', 'images')"
                    />

                    @if (sealPhotoUrl) {
                      <div class="p-3 rounded-xl border border-border bg-surface transition-all space-y-2.5">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div
                            role="button"
                            tabindex="0"
                            [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'sealImage')"
                            [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'sealImage')"
                            (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'sealImage')"
                            (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'sealImage')"
                            (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'sealImage', 'sealImage', 'images')"
                            (click)="viewDocument(sealPhotoUrl, 'Seal Photo')"
                            (keydown.enter)="viewDocument(sealPhotoUrl, 'Seal Photo')"
                            class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high hover:bg-surface-low border border-border text-left transition-all group cursor-pointer"
                            title="Click to view Seal Photo in viewer, or drag & drop file to replace"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                🔒
                              </div>
                              <div class="min-w-0">
                                <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                  Seal Photo
                                </app-text>
                                <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                  <lucide-icon name="Eye" class="w-3 h-3"></lucide-icon> Click to view in viewer
                                </app-text>
                              </div>
                            </div>

                            <div class="flex-shrink-0 ml-3">
                              @if (sealPhotoDoc?.status === 'approved') {
                                <app-badge variant="success" badgeStyle="soft" prefixIcon="CheckCircle2" size="xs">Approved</app-badge>
                              } @else if (sealPhotoDoc?.status === 'rejected') {
                                <app-badge variant="danger" badgeStyle="soft" prefixIcon="FileX" size="xs">Rejected</app-badge>
                              } @else {
                                <app-badge variant="warning" badgeStyle="soft" prefixIcon="Clock" size="xs">Pending</app-badge>
                              }
                            </div>
                          </div>

                          <div class="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                            @if (sealPhotoDoc?.status === 'rejected') {
                              <app-button
                                variant="danger"
                                shape="pill"
                                size="xs"
                                icon="UploadCloud"
                                [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'sealImage'"
                                (click)="sealImageInput.click()"
                                title="Select file to resubmit"
                              >
                                Resubmit
                              </app-button>
                            } @else {
                              @if (isReviewStage(stopForm.stop)) {
                                @if (sealPhotoDoc?.status !== 'approved') {
                                  <app-button
                                    variant="success"
                                    shape="pill"
                                    size="xs"
                                    icon="Check"
                                    title="Approve Seal Photo"
                                    (click)="approveSingleDocument(stopForm.stop.stopNumber, 'sealImage')"
                                  >
                                    Approve
                                  </app-button>
                                }
                                <app-button
                                  variant="danger"
                                  shape="pill"
                                  size="xs"
                                  icon="FileX"
                                  title="Reject Seal Photo"
                                  (click)="startDocRejection(stopForm.stop.stopNumber, 'sealImage')"
                                >
                                  Reject
                                </app-button>
                              }

                              <app-button
                                variant="secondary"
                                shape="pill"
                                size="xs"
                                icon="RotateCcw"
                                [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'sealImage'"
                                (click)="sealImageInput.click()"
                                title="Replace this document"
                              >
                                Replace
                              </app-button>
                            }
                          </div>
                        </div>

                        @if (sealPhotoDoc?.status === 'rejected' && sealPhotoDoc?.rejectionReason) {
                          <div class="p-2.5 rounded-lg bg-surface-high border border-danger/40 text-xs text-danger flex items-start gap-2">
                            <lucide-icon name="FileX" class="w-4 h-4 flex-shrink-0 mt-0.5"></lucide-icon>
                            <div><strong>Rejection reason:</strong> {{ sealPhotoDoc.rejectionReason }}</div>
                          </div>
                        }

                        @if (rejectingDoc()?.stopNumber === stopForm.stop.stopNumber && rejectingDoc()?.docKey === 'sealImage') {
                          <div class="p-3.5 rounded-xl bg-surface border border-danger/40 space-y-3 shadow-sm">
                            <div class="flex items-center gap-2">
                              <lucide-icon name="FileX" class="w-4 h-4 text-danger"></lucide-icon>
                              <app-text size="xs" weight="semibold" color="danger">Specify reason for rejecting Seal Photo</app-text>
                            </div>
                            <app-input
                              [value]="docRejectionReason()"
                              (valueChange)="docRejectionReason.set($event ?? '')"
                              placeholder="e.g., seal number unreadable, seal broken"
                            ></app-input>
                            <div class="flex gap-2 justify-end pt-2">
                              <app-button variant="ghost" shape="pill" size="xs" (click)="cancelDocRejection()">Cancel</app-button>
                              <app-button variant="danger" shape="pill" size="xs" icon="FileX" (click)="confirmDocRejection(stopForm.stop.stopNumber, 'sealImage')">Confirm Reject</app-button>
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="p-3 rounded-xl border border-border bg-surface transition-all">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [formGroup]="stopForm.form">
                          <div
                            role="button"
                            tabindex="0"
                            [class.opacity-60]="stopForm.form.get('noSealImage')?.value"
                            [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'sealImage')"
                            [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'sealImage')"
                            (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'sealImage')"
                            (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'sealImage')"
                            (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'sealImage', 'sealImage', 'images')"
                            (click)="!stopForm.form.get('noSealImage')?.value && sealImageInput.click()"
                            (keydown.enter)="!stopForm.form.get('noSealImage')?.value && sealImageInput.click()"
                            class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high border border-dashed border-border text-left transition-all group cursor-pointer"
                            [title]="stopForm.form.get('noSealImage')?.value ? 'Document marked as not available' : 'Click or drag & drop to upload file'"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                🔒
                              </div>
                              <div class="min-w-0">
                                <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                  Seal Photo
                                </app-text>
                                <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                  @if (uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'sealImage') {
                                    <lucide-icon name="UploadCloud" class="w-3 h-3 animate-bounce text-primary"></lucide-icon>
                                    Uploading {{ uploadingDoc()?.progress }}%...
                                  } @else if (stopForm.form.get('noSealImage')?.value) {
                                    Not required / available
                                  } @else {
                                    <lucide-icon name="UploadCloud" class="w-3 h-3"></lucide-icon> Click or drag & drop to upload (Image)
                                  }
                                </app-text>
                              </div>
                            </div>

                            <div class="flex-shrink-0 ml-3">
                              @if (stopForm.form.get('noSealImage')?.value) {
                                <app-badge variant="default" badgeStyle="soft" size="xs">N/A</app-badge>
                              } @else {
                                <app-badge variant="default" badgeStyle="outline" size="xs">Missing</app-badge>
                              }
                            </div>
                          </div>

                          <div class="flex items-center gap-3 flex-shrink-0 self-end sm:self-auto">
                            <app-button
                              variant="primary"
                              shape="pill"
                              size="xs"
                              icon="UploadCloud"
                              [disabled]="stopForm.form.get('noSealImage')?.value"
                              [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'sealImage'"
                              (click)="sealImageInput.click()"
                              title="Select file to upload"
                            >
                              Upload
                            </app-button>
                            <app-checkbox
                              formControlName="noSealImage"
                              label="N/A"
                              class="block"
                            ></app-checkbox>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Temperature Reading Card (if Reefer) -->
                    @if (isReeferLoad()) {
                      @let tempUrl = getDocPreviewUrl(stopForm, 'temperatureImage', 'temperatureImage');
                      @let tempDoc = stopForm.stop.documents?.temperatureImage;

                      <input
                        #tempImageInput
                        type="file"
                        class="hidden"
                        accept="image/*"
                        (change)="handleDirectDocUpload($event, stopForm.stop.stopNumber, 'temperatureImage', 'temperatureImage', 'images')"
                      />

                      @if (tempUrl) {
                        <div class="p-3 rounded-xl border border-border bg-surface transition-all space-y-2.5">
                          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div
                              role="button"
                              tabindex="0"
                              [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'temperatureImage')"
                              [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'temperatureImage')"
                              (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'temperatureImage')"
                              (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'temperatureImage')"
                              (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'temperatureImage', 'temperatureImage', 'images')"
                              (click)="viewDocument(tempUrl, 'Temperature Reading')"
                              (keydown.enter)="viewDocument(tempUrl, 'Temperature Reading')"
                              class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high hover:bg-surface-low border border-border text-left transition-all group cursor-pointer"
                              title="Click to view Temperature Reading in viewer, or drag & drop file to replace"
                            >
                              <div class="flex items-center gap-3 min-w-0">
                                <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                  ❄️
                                </div>
                                <div class="min-w-0">
                                  <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                    Temperature Reading
                                  </app-text>
                                  <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                    <lucide-icon name="Eye" class="w-3 h-3"></lucide-icon> Click to view in viewer
                                  </app-text>
                                </div>
                              </div>

                              <div class="flex-shrink-0 ml-3">
                                @if (tempDoc?.status === 'approved') {
                                  <app-badge variant="success" badgeStyle="soft" prefixIcon="CheckCircle2" size="xs">Approved</app-badge>
                                } @else if (tempDoc?.status === 'rejected') {
                                  <app-badge variant="danger" badgeStyle="soft" prefixIcon="FileX" size="xs">Rejected</app-badge>
                                } @else {
                                  <app-badge variant="warning" badgeStyle="soft" prefixIcon="Clock" size="xs">Pending</app-badge>
                                }
                              </div>
                            </div>

                            <div class="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                              @if (tempDoc?.status === 'rejected') {
                                <app-button
                                  variant="danger"
                                  shape="pill"
                                  size="xs"
                                  icon="UploadCloud"
                                  [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'temperatureImage'"
                                  (click)="tempImageInput.click()"
                                  title="Select file to resubmit"
                                >
                                  Resubmit
                                </app-button>
                              } @else {
                                @if (isReviewStage(stopForm.stop)) {
                                  @if (tempDoc?.status !== 'approved') {
                                    <app-button
                                      variant="success"
                                      shape="pill"
                                      size="xs"
                                      icon="Check"
                                      title="Approve Temperature Reading"
                                      (click)="approveSingleDocument(stopForm.stop.stopNumber, 'temperatureImage')"
                                    >
                                      Approve
                                    </app-button>
                                  }
                                  <app-button
                                    variant="danger"
                                    shape="pill"
                                    size="xs"
                                    icon="FileX"
                                    title="Reject Temperature Reading"
                                    (click)="startDocRejection(stopForm.stop.stopNumber, 'temperatureImage')"
                                  >
                                    Reject
                                  </app-button>
                                }

                                <app-button
                                  variant="secondary"
                                  shape="pill"
                                  size="xs"
                                  icon="RotateCcw"
                                  [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'temperatureImage'"
                                  (click)="tempImageInput.click()"
                                  title="Replace this document"
                                >
                                  Replace
                                </app-button>
                              }
                            </div>
                          </div>

                          @if (tempDoc?.status === 'rejected' && tempDoc?.rejectionReason) {
                            <div class="p-2.5 rounded-lg bg-surface-high border border-danger/40 text-xs text-danger flex items-start gap-2">
                              <lucide-icon name="FileX" class="w-4 h-4 flex-shrink-0 mt-0.5"></lucide-icon>
                              <div><strong>Rejection reason:</strong> {{ tempDoc.rejectionReason }}</div>
                            </div>
                          }

                          @if (rejectingDoc()?.stopNumber === stopForm.stop.stopNumber && rejectingDoc()?.docKey === 'temperatureImage') {
                            <div class="p-3.5 rounded-xl bg-surface border border-danger/40 space-y-3 shadow-sm">
                              <div class="flex items-center gap-2">
                                <lucide-icon name="FileX" class="w-4 h-4 text-danger"></lucide-icon>
                                <app-text size="xs" weight="semibold" color="danger">Specify reason for rejecting Temperature Reading</app-text>
                              </div>
                              <app-input
                                [value]="docRejectionReason()"
                                (valueChange)="docRejectionReason.set($event ?? '')"
                                placeholder="e.g., temperature out of acceptable range"
                              ></app-input>
                              <div class="flex gap-2 justify-end pt-2">
                                <app-button variant="ghost" shape="pill" size="xs" (click)="cancelDocRejection()">Cancel</app-button>
                                <app-button variant="danger" shape="pill" size="xs" icon="FileX" (click)="confirmDocRejection(stopForm.stop.stopNumber, 'temperatureImage')">Confirm Reject</app-button>
                              </div>
                            </div>
                          }
                        </div>
                      } @else {
                        <div class="p-3 rounded-xl border border-border bg-surface transition-all">
                          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [formGroup]="stopForm.form">
                            <div
                              role="button"
                              tabindex="0"
                              [class.opacity-60]="stopForm.form.get('noTemperatureImage')?.value"
                              [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'temperatureImage')"
                              [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'temperatureImage')"
                              (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'temperatureImage')"
                              (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'temperatureImage')"
                              (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'temperatureImage', 'temperatureImage', 'images')"
                              (click)="!stopForm.form.get('noTemperatureImage')?.value && tempImageInput.click()"
                              (keydown.enter)="!stopForm.form.get('noTemperatureImage')?.value && tempImageInput.click()"
                              class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high border border-dashed border-border text-left transition-all group cursor-pointer"
                              [title]="stopForm.form.get('noTemperatureImage')?.value ? 'Document marked as not available' : 'Click or drag & drop to upload file'"
                            >
                              <div class="flex items-center gap-3 min-w-0">
                                <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                  ❄️
                                </div>
                                <div class="min-w-0">
                                  <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                    Temperature Reading
                                  </app-text>
                                  <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                    @if (uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'temperatureImage') {
                                      <lucide-icon name="UploadCloud" class="w-3 h-3 animate-bounce text-primary"></lucide-icon>
                                      Uploading {{ uploadingDoc()?.progress }}%...
                                    } @else if (stopForm.form.get('noTemperatureImage')?.value) {
                                      Not required / available
                                    } @else {
                                      <lucide-icon name="UploadCloud" class="w-3 h-3"></lucide-icon> Click or drag & drop to upload (Image)
                                    }
                                  </app-text>
                                </div>
                              </div>

                              <div class="flex-shrink-0 ml-3">
                                @if (stopForm.form.get('noTemperatureImage')?.value) {
                                  <app-badge variant="default" badgeStyle="soft" size="xs">N/A</app-badge>
                                } @else {
                                  <app-badge variant="default" badgeStyle="outline" size="xs">Missing</app-badge>
                                }
                              </div>
                            </div>

                            <div class="flex items-center gap-3 flex-shrink-0 self-end sm:self-auto">
                              <app-button
                                variant="primary"
                                shape="pill"
                                size="xs"
                                icon="UploadCloud"
                                [disabled]="stopForm.form.get('noTemperatureImage')?.value"
                                [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'temperatureImage'"
                                (click)="tempImageInput.click()"
                                title="Select file to upload"
                              >
                                Upload
                              </app-button>
                              <app-checkbox
                                formControlName="noTemperatureImage"
                                label="N/A"
                                class="block"
                              ></app-checkbox>
                            </div>
                          </div>
                        </div>
                      }
                    }
                  }

                  @if (stopForm.stop.stopType === 'delivery' || stopForm.stop.stopType === 'both') {
                    <!-- POD Card -->
                    @let podUrl = getDocPreviewUrl(stopForm, 'proofOfDelivery', 'pod');
                    @let podDoc = stopForm.stop.documents?.proofOfDelivery;

                    <input
                      #podInput
                      type="file"
                      class="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      (change)="handleDirectDocUpload($event, stopForm.stop.stopNumber, 'proofOfDelivery', 'pod', 'documents')"
                    />

                    @if (podUrl) {
                      <div class="p-3 rounded-xl border border-border bg-surface transition-all space-y-2.5">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div
                            role="button"
                            tabindex="0"
                            [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'proofOfDelivery')"
                            [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'proofOfDelivery')"
                            (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'proofOfDelivery')"
                            (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'proofOfDelivery')"
                            (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'proofOfDelivery', 'pod', 'documents')"
                            (click)="viewDocument(podUrl, 'Proof of Delivery')"
                            (keydown.enter)="viewDocument(podUrl, 'Proof of Delivery')"
                            class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high hover:bg-surface-low border border-border text-left transition-all group cursor-pointer"
                            title="Click to view Proof of Delivery in viewer, or drag & drop file to replace"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                ✍️
                              </div>
                              <div class="min-w-0">
                                <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                  Proof of Delivery
                                </app-text>
                                <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                  <lucide-icon name="Eye" class="w-3 h-3"></lucide-icon> Click to view in viewer
                                </app-text>
                              </div>
                            </div>

                            <div class="flex-shrink-0 ml-3">
                              @if (podDoc?.status === 'approved') {
                                <app-badge variant="success" badgeStyle="soft" prefixIcon="CheckCircle2" size="xs">Approved</app-badge>
                              } @else if (podDoc?.status === 'rejected') {
                                <app-badge variant="danger" badgeStyle="soft" prefixIcon="FileX" size="xs">Rejected</app-badge>
                              } @else {
                                <app-badge variant="warning" badgeStyle="soft" prefixIcon="Clock" size="xs">Pending</app-badge>
                              }
                            </div>
                          </div>

                          <div class="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                            @if (podDoc?.status === 'rejected') {
                              <app-button
                                variant="danger"
                                shape="pill"
                                size="xs"
                                icon="UploadCloud"
                                [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'proofOfDelivery'"
                                (click)="podInput.click()"
                                title="Select file to resubmit"
                              >
                                Resubmit
                              </app-button>
                            } @else {
                              @if (isReviewStage(stopForm.stop)) {
                                @if (podDoc?.status !== 'approved') {
                                  <app-button
                                    variant="success"
                                    shape="pill"
                                    size="xs"
                                    icon="Check"
                                    title="Approve Proof of Delivery"
                                    (click)="approveSingleDocument(stopForm.stop.stopNumber, 'proofOfDelivery')"
                                  >
                                    Approve
                                  </app-button>
                                }
                                <app-button
                                  variant="danger"
                                  shape="pill"
                                  size="xs"
                                  icon="FileX"
                                  title="Reject Proof of Delivery"
                                  (click)="startDocRejection(stopForm.stop.stopNumber, 'proofOfDelivery')"
                                >
                                  Reject
                                </app-button>
                              }

                              <app-button
                                variant="secondary"
                                shape="pill"
                                size="xs"
                                icon="RotateCcw"
                                [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'proofOfDelivery'"
                                (click)="podInput.click()"
                                title="Replace this document"
                              >
                                Replace
                              </app-button>
                            }
                          </div>
                        </div>

                        @if (podDoc?.status === 'rejected' && podDoc?.rejectionReason) {
                          <div class="p-2.5 rounded-lg bg-surface-high border border-danger/40 text-xs text-danger flex items-start gap-2">
                            <lucide-icon name="FileX" class="w-4 h-4 flex-shrink-0 mt-0.5"></lucide-icon>
                            <div><strong>Rejection reason:</strong> {{ podDoc.rejectionReason }}</div>
                          </div>
                        }

                        @if (rejectingDoc()?.stopNumber === stopForm.stop.stopNumber && rejectingDoc()?.docKey === 'proofOfDelivery') {
                          <div class="p-3.5 rounded-xl bg-surface border border-danger/40 space-y-3 shadow-sm">
                            <div class="flex items-center gap-2">
                              <lucide-icon name="FileX" class="w-4 h-4 text-danger"></lucide-icon>
                              <app-text size="xs" weight="semibold" color="danger">Specify reason for rejecting Proof of Delivery</app-text>
                            </div>
                            <app-input
                              [value]="docRejectionReason()"
                              (valueChange)="docRejectionReason.set($event ?? '')"
                              placeholder="e.g., missing receiver signature, page cut off"
                            ></app-input>
                            <div class="flex gap-2 justify-end pt-2">
                              <app-button variant="ghost" shape="pill" size="xs" (click)="cancelDocRejection()">Cancel</app-button>
                              <app-button variant="danger" shape="pill" size="xs" icon="FileX" (click)="confirmDocRejection(stopForm.stop.stopNumber, 'proofOfDelivery')">Confirm Reject</app-button>
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="p-3 rounded-xl border border-border bg-surface transition-all">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [formGroup]="stopForm.form">
                          <div
                            role="button"
                            tabindex="0"
                            [class.opacity-60]="stopForm.form.get('noPod')?.value"
                            [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'proofOfDelivery')"
                            [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'proofOfDelivery')"
                            (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'proofOfDelivery')"
                            (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'proofOfDelivery')"
                            (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'proofOfDelivery', 'pod', 'documents')"
                            (click)="!stopForm.form.get('noPod')?.value && podInput.click()"
                            (keydown.enter)="!stopForm.form.get('noPod')?.value && podInput.click()"
                            class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high border border-dashed border-border text-left transition-all group cursor-pointer"
                            [title]="stopForm.form.get('noPod')?.value ? 'Document marked as not available' : 'Click or drag & drop to upload file'"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                                ✍️
                              </div>
                              <div class="min-w-0">
                                <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                  Proof of Delivery
                                </app-text>
                                <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                  @if (uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'proofOfDelivery') {
                                    <lucide-icon name="UploadCloud" class="w-3 h-3 animate-bounce text-primary"></lucide-icon>
                                    Uploading {{ uploadingDoc()?.progress }}%...
                                  } @else if (stopForm.form.get('noPod')?.value) {
                                    Not required / available
                                  } @else {
                                    <lucide-icon name="UploadCloud" class="w-3 h-3"></lucide-icon> Click or drag & drop to upload (PDF, PNG, JPG)
                                  }
                                </app-text>
                              </div>
                            </div>

                            <div class="flex-shrink-0 ml-3">
                              @if (stopForm.form.get('noPod')?.value) {
                                <app-badge variant="default" badgeStyle="soft" size="xs">N/A</app-badge>
                              } @else {
                                <app-badge variant="default" badgeStyle="outline" size="xs">Missing</app-badge>
                              }
                            </div>
                          </div>

                          <div class="flex items-center gap-3 flex-shrink-0 self-end sm:self-auto">
                            <app-button
                              variant="primary"
                              shape="pill"
                              size="xs"
                              icon="UploadCloud"
                              [disabled]="stopForm.form.get('noPod')?.value"
                              [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'proofOfDelivery'"
                              (click)="podInput.click()"
                              title="Select file to upload"
                            >
                              Upload
                            </app-button>
                            <app-checkbox
                              formControlName="noPod"
                              label="N/A"
                              class="block"
                            ></app-checkbox>
                          </div>
                        </div>
                      </div>
                    }
                  }

                  <!-- Lumper Receipt & Accessorial Details Card -->
                  @let lumperUrl = getDocPreviewUrl(stopForm, 'lumperReceipt', 'lumperReceipt');
                  @let lumperDoc = stopForm.stop.documents?.lumperReceipt;

                  <input
                    #lumperInput
                    type="file"
                    class="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    (change)="handleDirectDocUpload($event, stopForm.stop.stopNumber, 'lumperReceipt', 'lumperReceipt', 'documents')"
                  />

                  @if (lumperUrl) {
                    <div class="p-3 rounded-xl border border-border bg-surface transition-all space-y-2.5">
                      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div
                          role="button"
                          tabindex="0"
                          [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'lumperReceipt')"
                          [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'lumperReceipt')"
                          (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'lumperReceipt')"
                          (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'lumperReceipt')"
                          (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'lumperReceipt', 'lumperReceipt', 'documents')"
                          (click)="viewDocument(lumperUrl, 'Lumper Receipt')"
                          (keydown.enter)="viewDocument(lumperUrl, 'Lumper Receipt')"
                          class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high hover:bg-surface-low border border-border text-left transition-all group cursor-pointer"
                          title="Click to view Lumper Receipt in viewer, or drag & drop file to replace"
                        >
                          <div class="flex items-center gap-3 min-w-0">
                            <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                              🧾
                            </div>
                            <div class="min-w-0">
                              <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                Lumper Receipt
                              </app-text>
                              <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                <lucide-icon name="Eye" class="w-3 h-3"></lucide-icon> Click to view in viewer
                              </app-text>
                            </div>
                          </div>

                          <div class="flex-shrink-0 ml-3">
                            @if (lumperDoc?.status === 'approved') {
                              <app-badge variant="success" badgeStyle="soft" prefixIcon="CheckCircle2" size="xs">Approved</app-badge>
                            } @else if (lumperDoc?.status === 'rejected') {
                              <app-badge variant="danger" badgeStyle="soft" prefixIcon="FileX" size="xs">Rejected</app-badge>
                            } @else {
                              <app-badge variant="warning" badgeStyle="soft" prefixIcon="Clock" size="xs">Pending</app-badge>
                            }
                          </div>
                        </div>

                        <div class="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                          @if (lumperDoc?.status === 'rejected') {
                            <app-button
                              variant="danger"
                              shape="pill"
                              size="xs"
                              icon="UploadCloud"
                              [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'lumperReceipt'"
                              (click)="lumperInput.click()"
                              title="Select file to resubmit"
                            >
                              Resubmit
                            </app-button>
                          } @else {
                            @if (lumperDoc?.status !== 'approved') {
                              <app-button
                                variant="success"
                                shape="pill"
                                size="xs"
                                icon="Check"
                                title="Approve Lumper Receipt"
                                (click)="approveSingleDocument(stopForm.stop.stopNumber, 'lumperReceipt')"
                              >
                                Approve
                              </app-button>
                            }
                            <app-button
                              variant="danger"
                              shape="pill"
                              size="xs"
                              icon="FileX"
                              title="Reject Lumper Receipt"
                              (click)="startDocRejection(stopForm.stop.stopNumber, 'lumperReceipt')"
                            >
                              Reject
                            </app-button>

                            <app-button
                              variant="secondary"
                              shape="pill"
                              size="xs"
                              icon="RotateCcw"
                              [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'lumperReceipt'"
                              (click)="lumperInput.click()"
                              title="Replace this document"
                            >
                              Replace
                            </app-button>
                          }
                        </div>
                      </div>

                      @if (lumperDoc?.status === 'rejected' && lumperDoc?.rejectionReason) {
                        <div class="p-2.5 rounded-lg bg-surface-high border border-danger text-xs text-danger flex items-start gap-2">
                          <lucide-icon name="FileX" class="w-4 h-4 flex-shrink-0 mt-0.5"></lucide-icon>
                          <div><strong>Rejection reason:</strong> {{ lumperDoc.rejectionReason }}</div>
                        </div>
                      }

                      @if (rejectingDoc()?.stopNumber === stopForm.stop.stopNumber && rejectingDoc()?.docKey === 'lumperReceipt') {
                        <div class="p-3.5 rounded-xl bg-surface border border-danger space-y-3 shadow-sm">
                          <div class="flex items-center gap-2">
                            <lucide-icon name="FileX" class="w-4 h-4 text-danger"></lucide-icon>
                            <app-text size="xs" weight="semibold" color="danger">Specify reason for rejecting Lumper Receipt</app-text>
                          </div>
                          <app-input
                            [value]="docRejectionReason()"
                            (valueChange)="docRejectionReason.set($event ?? '')"
                            placeholder="e.g., illegible amount, missing facility stamp"
                          ></app-input>
                          <div class="flex gap-2 justify-end pt-2">
                            <app-button variant="ghost" shape="pill" size="xs" (click)="cancelDocRejection()">Cancel</app-button>
                            <app-button variant="danger" shape="pill" size="xs" icon="FileX" (click)="confirmDocRejection(stopForm.stop.stopNumber, 'lumperReceipt')">Confirm Reject</app-button>
                          </div>
                        </div>
                      }
                    </div>
                  } @else {
                    <div class="p-3 rounded-xl border border-border bg-surface transition-all">
                      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [formGroup]="stopForm.form">
                        <div
                          role="button"
                          tabindex="0"
                          [class.opacity-60]="stopForm.form.get('noLumperReceipt')?.value"
                          [class.border-primary]="isDraggingOver(stopForm.stop.stopNumber, 'lumperReceipt')"
                          [class.bg-primary/5]="isDraggingOver(stopForm.stop.stopNumber, 'lumperReceipt')"
                          (dragover)="onDocDragOver($event, stopForm.stop.stopNumber, 'lumperReceipt')"
                          (dragleave)="onDocDragLeave($event, stopForm.stop.stopNumber, 'lumperReceipt')"
                          (drop)="onDocDrop($event, stopForm.stop.stopNumber, 'lumperReceipt', 'lumperReceipt', 'documents')"
                          (click)="!stopForm.form.get('noLumperReceipt')?.value && lumperInput.click()"
                          (keydown.enter)="!stopForm.form.get('noLumperReceipt')?.value && lumperInput.click()"
                          class="flex-1 flex items-center justify-between p-2.5 rounded-lg bg-surface-high border border-dashed border-border text-left transition-all group cursor-pointer"
                          [title]="stopForm.form.get('noLumperReceipt')?.value ? 'Document marked as not available' : 'Click or drag & drop to upload file'"
                        >
                          <div class="flex items-center gap-3 min-w-0">
                            <div class="w-10 h-10 rounded-lg bg-surface flex items-center justify-center text-xl border border-border group-hover:scale-110 transition-transform flex-shrink-0 shadow-xs">
                              🧾
                            </div>
                            <div class="min-w-0">
                              <app-text size="sm" weight="semibold" class="truncate group-hover:text-primary transition-colors">
                                Lumper Receipt
                              </app-text>
                              <app-text size="xs" color="muted" class="flex items-center gap-1 truncate">
                                @if (uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'lumperReceipt') {
                                  <lucide-icon name="UploadCloud" class="w-3 h-3 animate-bounce text-primary"></lucide-icon>
                                  Uploading {{ uploadingDoc()?.progress }}%...
                                } @else if (stopForm.form.get('noLumperReceipt')?.value) {
                                  Not required / available
                                } @else {
                                  <lucide-icon name="UploadCloud" class="w-3 h-3"></lucide-icon> Optional - Click or drag & drop to upload (PDF, PNG, JPG)
                                }
                              </app-text>
                            </div>
                          </div>

                          <div class="flex-shrink-0 ml-3">
                            @if (stopForm.form.get('noLumperReceipt')?.value) {
                              <app-badge variant="default" badgeStyle="soft" size="xs">N/A</app-badge>
                            } @else {
                              <app-badge variant="default" badgeStyle="outline" size="xs">Optional</app-badge>
                            }
                          </div>
                        </div>

                        <div class="flex items-center gap-3 flex-shrink-0 self-end sm:self-auto">
                          <app-button
                            variant="primary"
                            shape="pill"
                            size="xs"
                            icon="UploadCloud"
                            [disabled]="stopForm.form.get('noLumperReceipt')?.value"
                            [loading]="uploadingDoc()?.stopNumber === stopForm.stop.stopNumber && uploadingDoc()?.docKey === 'lumperReceipt'"
                            (click)="lumperInput.click()"
                            title="Select file to upload"
                          >
                            Upload
                          </app-button>
                          <app-checkbox
                            formControlName="noLumperReceipt"
                            label="N/A"
                            class="block"
                          ></app-checkbox>
                        </div>
                      </div>
                    </div>
                  }

                  <!-- Lumper Fee & Payer Inputs -->
                  <div class="p-3.5 rounded-xl border border-border bg-surface-high space-y-3" [formGroup]="stopForm.form">
                    <div class="flex items-center justify-between">
                      <app-text size="xs" weight="bold">Lumper Fee & Reimbursement</app-text>
                      <app-text size="xs" color="muted">Receipt requires fee > $0</app-text>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div class="space-y-1">
                        <label class="text-xs text-muted block">Lumper Fee Amount ($)</label>
                        <app-input
                          type="number"
                          formControlName="lumperFee"
                          placeholder="0.00"
                          size="sm"
                        ></app-input>
                      </div>

                      <div class="space-y-1">
                        <label class="text-xs text-muted block">Paid By</label>
                        <app-select
                          [options]="lumperPayerOptions"
                          formControlName="lumperPaidBy"
                          size="sm"
                        ></app-select>
                      </div>
                    </div>

                    @if (stopForm.form.get('lumperFee')?.value && stopForm.form.get('lumperFee')?.value > 0) {
                      <div class="text-xs text-muted flex items-center gap-2 pt-1 border-t border-border/60">
                        @if (stopForm.form.get('lumperPaidBy')?.value === 'driver') {
                          <span class="text-primary font-medium">✓ Auto-syncs to Driver Mobile App & Payroll settlement</span>
                        } @else if (stopForm.form.get('lumperPaidBy')?.value === 'carrier') {
                          <span class="text-foreground">✓ Auto-syncs to Invoice Pricing as reimbursable accessorial</span>
                        } @else {
                          <span>Directly covered by broker / facility</span>
                        }
                      </div>
                    }
                  </div>
                </div>
              </div>
            }
          }

          <!-- Manual Override -->
          @if (!stopForm.isCompleted && stopForm.isCurrentStop) {
          <div class="mt-6 pt-4 border-t border-border flex justify-end">
            <app-button 
              variant="ghost" 
              size="sm" 
              (click)="forceCompleteStop(index)"
              title="Mark this stop as completed without filling all details"
            >
              Mark Stop as Completed (Override)
            </app-button>
          </div>
          }
        </div>
      </div>
    </div>
    }
  </div>
  }

  <!-- Progress Summary -->
  <div class="p-3 rounded-lg border border-border bg-surface">
    <div class="flex items-center justify-between mb-2">
      <app-text size="xs" weight="semibold">Overall Progress</app-text>
      <app-text size="xs" color="muted">
        {{ completedStopsCount() }} of {{ totalStopsCount() }} stops completed
      </app-text>
    </div>
    <div class="flex gap-1.5">
      @for (sf of stopForms(); track sf.stop.stopNumber) {
      <div
        class="flex-1 h-1.5 rounded-full transition-colors"
        [ngClass]="{
          'bg-success': sf.isCompleted,
          'bg-primary': sf.isCurrentStop && !sf.isCompleted,
          'bg-secondary': !sf.isCurrentStop && !sf.isCompleted
        }"
      ></div>
      }
    </div>
  </div>
</div>

<!-- Modal Actions -->
<div slot="actions" class="flex gap-2 justify-end w-full pt-4 mt-2 border-t border-border">
  <app-button variant="outline" (click)="cancel()">
    Close
  </app-button>
</div>
